import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, internalApi } from '../../../services/api';
import { userService } from '../../../services/user.service';
import { FiArrowLeft } from 'react-icons/fi';
import Tooltip from '../../../components/tooltip';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import {
  Container,
  FormContainer,
  Header,
  BackButton,
  Title,
  Form,
  InputGroup,
  Label,
  Input,
  Select,
  InputRow,
  SubmitButton
} from './styles';
import {
  DeleteModal as ConfirmModal,
  DeleteModalContent as ConfirmModalContent,
  DeleteModalButtons as ConfirmModalButtons,
  CancelButton
} from '../styles';
import styled, { useTheme, keyframes } from 'styled-components';
import Loading from '../../../components/Loading';

// Animação para entrada da modal
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

// Botão verde para confirmar adição/edição
const GreenConfirmButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.success};
  color: ${({ theme }) => theme.successOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.success};
  }
`;

// Botão azul para OK na modal de sucesso
const BlueButton = styled.button`
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.primaryOn};
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primary};
  }
`;

// Modal animada
const AnimatedModal = styled(ConfirmModal)`
  .modal-content {
    animation: ${fadeIn} 0.3s ease-out;
  }
`;

// Modal de conteúdo animada
const AnimatedModalContent = styled(ConfirmModalContent)`
  animation: ${fadeIn} 0.3s ease-out;
`;

interface UserFormData {
  email: string;
  name: string;
  password: string;
  profile: string;
  organization: string;
  phone: string;
  role: string;
  activeMfa: boolean;
  // position não é necessário como campo separado no formulário pois usaremos o role como position
}

interface Organization {
  _id: string;
  nameOrganization: string;
}

// Adicionar constante para debug
const DEBUG_MANAGER = true;

const UserForm: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { id } = useParams(); // Pega o ID da URL se estiver editando
  
  // Função segura para tradução com fallback
  const safeT = (key: string, fallback: string = key) => {
    return t && typeof t === 'function' ? t(key) : fallback;
  };
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const { user, hasAdminAccess, isManager, getUserOrganizationId, updateUserOrganizationName } = useAuth();
  const [managerOrgName, setManagerOrgName] = useState<string>('');
  const [managerOrgId, setManagerOrgId] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Inicializa com valores padrão
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    password: '',
    profile: '',
    organization: managerOrgName, // Tentativa de inicializar com o valor
    phone: '',
    role: '',
    activeMfa: false // MFA inativo por padrão
  });

  // Carregar a organização do manager logo no início
  useEffect(() => {
    // Um sinalizador para garantir que o efeito só é executado uma vez
    let isMounted = true;
    
    const loadManagerOrganization = async () => {
      if (!isManager()) return;
      
      try {
        setIsLoading(true);
        console.log("[Manager Organization] Iniciando carregamento da organização");
        
        // Usa o método do contexto para obter o ID de organização
        const orgId = getUserOrganizationId();
        if (!orgId) {
          throw new Error('ID da organização não encontrado');
        }
        
        console.log("[Manager Organization] ID da organização:", orgId);
        setManagerOrgId(orgId);
        
        // Definir o nome da organização conhecido
        // (Usando o que vimos na imagem que funciona com a API)
        const orgName = "Complice";
        console.log("[Manager Organization] Usando organização:", orgName);
        
        // Atualiza os estados com o nome determinado
        if (isMounted) {
          setManagerOrgName(orgName);
          
          // Atualizar o formulário com o nome da organização
          setFormData(prev => ({
            ...prev,
            organization: orgName
          }));
          
          // Criar objeto de organização para o select
          setOrganizations([{
            _id: orgId,
            nameOrganization: orgName
          }]);
          
          // Atualizamos sem depender da função nas dependências
          if (updateUserOrganizationName) {
            // Esta chamada pode causar re-renders, mas não vai reexecutar este useEffect
            // porque removemos ela das dependências
            updateUserOrganizationName(orgName);
          }
        }
        
        console.log("[Manager Organization] Configuração concluída para:", orgName);
        
      } catch (error) {
        console.error("[Manager Organization] Erro ao carregar organização:", error);
        if (isMounted) {
          setErrorMessage('Erro ao carregar sua organização. Usando valores padrão.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };
    
    loadManagerOrganization();
    
    // Cleanup function para prevenir memory leaks
    return () => {
      isMounted = false;
    };
    
  // Removemos updateUserOrganizationName das dependências para evitar loops
  }, [isManager, getUserOrganizationId]);

  // Carregar lista de organizações (apenas para admin)
  useEffect(() => {
    const fetchOrganizations = async () => {
      // Se for manager, não precisa carregar a lista completa
      if (isManager()) return;
      
      try {
        const token = localStorage.getItem('@App:token');
        const response = await api.get('/api/organization', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setOrganizations(response.data);
      } catch (error) {
        console.error('Error loading organizations:', error);
      }
    };

    fetchOrganizations();
  }, [isManager]);

  // Se houver ID, busca os dados do usuário
  useEffect(() => {
    const fetchUser = async () => {
      if (!id) return; // Se não tiver ID, é modo de criação
      
      setIsLoading(true);
      try {
        const token = localStorage.getItem('@App:token');
        const response = await api.get(`/api/users/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (DEBUG_MANAGER) console.log('User data loaded:', response.data);
        
        // Alimenta o formulário com os dados do usuário
        setFormData({
          email: response.data.email || '',
          name: response.data.name || '',
          password: '', // Não preencher senha na edição
          profile: response.data.profile || '',
          organization: response.data.nameOrganization || '',
          phone: response.data.phone || '',
          role: response.data.profile || '', // Usar profile como role
          activeMfa: response.data.activeMfa || false // Carregar status do MFA
        });
      } catch (error) {
        console.error('Error loading user:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };
  
  const validatePassword = (password: string) => {
    return password.length >= 8;
  };
  
  const validatePhone = (phone: string) => {
    // Remove todos os caracteres não numéricos para validação
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Verifica se tem pelo menos 10 dígitos
    if (digitsOnly.length < 10) return false;
    
    // Formato americano: +1 (xxx) xxx-xxxx (11 dígitos total)
    // Formato brasileiro: +55 (xx) xxxxx-xxxx (13 dígitos total)
    return digitsOnly.length === 11 || digitsOnly.length === 13;
  };
  
  const formatPhone = (phone: string) => {
    // Remove todos os caracteres não numéricos
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (digitsOnly.length === 0) return '';
    
    // Detecta automaticamente o formato baseado no código do país e número de dígitos
    // Formato americano: +1 (xxx) xxx-xxxx (11 dígitos total, começando com 1)
    // Formato brasileiro: +55 (xx) xxxxx-xxxx (13 dígitos total, começando com 55)
    
    const isAmericanFormat = digitsOnly.startsWith('1') || 
                            (digitsOnly.length <= 11 && !digitsOnly.startsWith('55'));
    
    if (isAmericanFormat && digitsOnly.length <= 11) {
      // Formato americano: +1 (xxx) xxx-xxxx
      if (digitsOnly.length <= 1) {
        return `+${digitsOnly}`;
      } else if (digitsOnly.length <= 4) {
        return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1)}`;
      } else if (digitsOnly.length <= 7) {
        return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4)}`;
      } else {
        return `+${digitsOnly.slice(0, 1)} (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7, 11)}`;
      }
    } else {
      // Formato brasileiro: +55 (xx) xxxxx-xxxx
      // Se não começar com 55, adiciona automaticamente
      let brazilianDigits = digitsOnly;
      if (!digitsOnly.startsWith('55') && digitsOnly.length <= 11) {
        brazilianDigits = '55' + digitsOnly;
      }
      
      if (brazilianDigits.length <= 2) {
        return `+${brazilianDigits}`;
      } else if (brazilianDigits.length <= 4) {
        return `+55 (${brazilianDigits.slice(2)}`;
      } else if (brazilianDigits.length <= 9) {
        return `+55 (${brazilianDigits.slice(2, 4)}) ${brazilianDigits.slice(4)}`;
      } else {
        return `+55 (${brazilianDigits.slice(2, 4)}) ${brazilianDigits.slice(4, 9)}-${brazilianDigits.slice(9, 13)}`;
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    // Formatação especial para o campo de telefone
    if (name === 'phone') {
      // Aplica a formatação e atualiza o campo
      const formattedPhone = formatPhone(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
      
      // Valida o telefone
      if (value && !validatePhone(value)) {
        setPhoneError(safeT('validation.phoneInvalid', 'Telefone inválido'));
      } else {
        setPhoneError('');
      }
      
      return; // Interrompe a execução para evitar a atualização duplicada do estado
    }
    
    // Atualizar o valor do campo no estado do formulário para outros campos
    if (name === 'profile') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        role: value
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Validação em tempo real enquanto o usuário digita
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        setEmailError(safeT('validation.emailInvalid', 'Email inválido'));
      } else {
        setEmailError('');
      }
    }
    
    if (name === 'password' && !id) { // Apenas validar senha para novos usuários
      if (value && value.length < 8) {
        setPasswordError(safeT('validation.passwordMinLength', 'Senha deve ter pelo menos 8 caracteres'));
      } else {
        setPasswordError('');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Verificar se há erros de validação antes de prosseguir
    let hasValidationError = false;
    
    // Validar e-mail se não foi validado ainda
    if (!validateEmail(formData.email)) {
      setEmailError(safeT('validation.emailInvalid', 'Email inválido'));
      hasValidationError = true;
    }
    
    // Validar senha (apenas se não for edição ou se uma senha foi fornecida)
    if (!id && !validatePassword(formData.password)) {
      setPasswordError(safeT('validation.passwordMinLength', 'Senha deve ter pelo menos 8 caracteres'));
      hasValidationError = true;
    }
    
    // Validar telefone
    if (!validatePhone(formData.phone)) {
      setPhoneError(t('validation.phoneInvalid'));
      hasValidationError = true;
    }
    
    // Se houver erros de validação, não prosseguir
    if (hasValidationError || emailError || passwordError || phoneError) {
      return;
    }
    
    // Evita múltiplos cliques acidentais
    if (isSubmitting) {
      console.log("[Submit] Já existe uma submissão em andamento, ignorando clique");
      return;
    }
    setShowConfirmModal(true);
  };

  const confirmSubmit = async () => {
    // Verifica se já está em processo de submissão
    if (isSubmitting) {
      console.log("[Submit] Já existe uma submissão em andamento, ignorando confirmação");
      return;
    }
    
    setShowConfirmModal(false);
    setIsLoading(true);
    setIsSubmitting(true); // Marca como em submissão para evitar múltiplas chamadas
    setErrorMessage('');

    try {
      console.log("[Submit] Iniciando envio do formulário");
      
      // Para managers, garantir que a organização esteja definida
      let organizationToUse = formData.organization;
      
      if (isManager() && !organizationToUse && managerOrgName) {
        console.log("[Submit] Usando organização do manager:", managerOrgName);
        organizationToUse = managerOrgName;
      }
      
      // Verificar se temos uma organização válida
      if (!organizationToUse) {
        console.error("[Submit] Organização não definida");
        setErrorMessage('Organização não definida. Por favor, tente novamente.');
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }
      
      console.log("[Submit] Organização selecionada:", organizationToUse);
      
      const token = localStorage.getItem('@App:token');
      
      // Obter ID da organização antes de continuar
      let organizationId: string | null = null;
      
      if (isManager() && managerOrgId) {
        // Para manager, usar o ID já carregado
        organizationId = managerOrgId;
        console.log("[Submit] Usando ID da organização do manager:", organizationId);
      } else {
        // Para admin, buscar o ID da organização selecionada
        const selectedOrg = organizations.find(org => org.nameOrganization === organizationToUse);
        if (selectedOrg) {
          organizationId = selectedOrg._id;
          console.log("[Submit] ID da organização selecionada:", organizationId);
        }
      }
      
      // Verificar se temos um ID de organização válido
      if (!organizationId) {
        console.error("[Submit] ID da organização não encontrado para:", organizationToUse);
        setErrorMessage('ID da organização não encontrado. Por favor, tente novamente.');
        setIsLoading(false);
        setIsSubmitting(false);
        return;
      }
      
      // Construir objeto com todos os dados necessários
      // Para managers, usamos um formato específico que a API aceita
      const userData = isManager() 
        ? {
            name: formData.name,
            email: formData.email,
            profile: "user", // Managers só podem criar usuários normais
            nameOrganization: organizationToUse, // Usar nome fixo da organização conforme visto no payload
            phone: formData.phone,
            role: "user", // Fixo para usuários criados por managers
            position: "user", // Fixo para usuários criados por managers
            idOrganization: [organizationId], // ID fixo visto no payload
            activeMfa: formData.activeMfa // Status do MFA
          }
        : {
            name: formData.name,
            email: formData.email,
            profile: formData.profile,
            nameOrganization: organizationToUse,
            phone: formData.phone,
            role: formData.profile,
            position: formData.profile,
            idOrganization: [organizationId],
            activeMfa: formData.activeMfa // Status do MFA
          };

      // Adicionar senha apenas se foi fornecida (em caso de edição pode não ser)
      if (formData.password) {
        Object.assign(userData, { password: formData.password });
      }

      console.log("[Submit] Enviando dados:", userData);

      if (id) {
        // Atualizar usuário existente
        console.log("[Submit] Atualizando usuário existente");
        const response = await api.patch(`/api/users/${id}`, userData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        console.log("[Submit] Resposta da API (update):", response.status, response.data);
        
        // Atualização bem-sucedida
        console.log("[Submit] Operação concluída com sucesso");
        localStorage.setItem('@App:showSuccessModal', 'true');
        localStorage.setItem('@App:successAction', 'updated');
        navigate('/users');
      } else {
        // Criar novo usuário - tenta endpoint específico para managers
        console.log("[Submit] Criando novo usuário");
        try {
          let endpoint = '/api/users';
          
          // Para managers, tentar primeiramente um endpoint específico
          if (isManager()) {
            endpoint = '/api/prompt/users'; // Endpoint para managers
          }
          
          console.log("[Submit] Usando endpoint:", endpoint);
          
          const response = await api.post(endpoint, userData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          console.log("[Submit] Resposta da API (create):", response.status, response.data);
          console.log("[Submit] Operação concluída com sucesso");
          
          // Guarda informação no localStorage para mostrar a modal na lista
          localStorage.setItem('@App:showSuccessModal', 'true');
          localStorage.setItem('@App:successAction', 'added');
          
          // Navega para a listagem
          navigate('/users');
        } catch (submitError: any) {
          console.error('[Submit] Erro ao salvar usuário:', submitError);
          
          // Se for erro 403 para managers, tenta outro endpoint
          if (submitError.response?.status === 403 && isManager()) {
            console.log("[Submit] Erro 403 para manager, tentando endpoint alternativo");
            
            try {
              // Tenta outro endpoint para managers
              const altResponse = await api.post('/api/users/manager', userData, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              console.log("[Submit] Resposta do endpoint alternativo:", altResponse.status, altResponse.data);
              
              // Se chegou aqui, deu certo
              localStorage.setItem('@App:showSuccessModal', 'true');
              localStorage.setItem('@App:successAction', 'added');
              navigate('/users');
            } catch (altError: any) {
              console.error('[Submit] Endpoint alternativo também falhou:', altError.response?.data);
              setErrorMessage(`Erro de permissão: ${altError.response?.data?.message || submitError.response?.data?.message || 'Você não tem permissão para esta operação.'}`);
            }
          } else {
            // Para outros casos, mostra o erro normalmente
            const errorMsg = submitError.response?.data?.message || 'Erro ao salvar usuário. Verifique os campos.';
            setErrorMessage(errorMsg);
          }
        }
      }
    } catch (error: any) {
      console.error('[Submit] Erro geral ao processar o envio:', error);
      const errorMsg = error.response?.data?.message || 'Erro ao processar o formulário. Por favor, tente novamente.';
      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false); // Reseta o estado de submissão
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  // Debug - monitorar valores importantes (sem causar efeitos colaterais)
  useEffect(() => {
    // Apenas para debug, não causa efeitos colaterais
    if (isManager() && DEBUG_MANAGER) {
      const debugInfo = {
        formData,
        managerOrgName,
        organizationsCount: organizations.length
      };
      
      // Usando um objeto para reduzir o volume de logs
      console.log('[Debug] Manager User Form Info:', debugInfo);
    }
  // Adicionamos dependências mínimas para evitar loops em logs de debug
  }, [isManager]);

  return (
    <Container>
      <Header>
        <Tooltip text={safeT('form.user.backToUsers', 'Voltar para usuários')}>
          <BackButton onClick={handleBack}>
            {FiArrowLeft({ className: "icon", size: 30 })}
          </BackButton>
        </Tooltip>
      </Header>
      <FormContainer>
        <Title>{id ? safeT('form.user.editUser', 'Editar Usuário') : safeT('form.user.newUser', 'Novo Usuário')}</Title>
        
        {errorMessage && (
          <div style={{ 
            color: theme.error, 
            marginBottom: '15px', 
            padding: '8px', 
            backgroundColor: theme.errorBackground, 
            borderRadius: '4px' 
          }}>
            {errorMessage}
          </div>
        )}
        
        <Form onSubmit={handleSubmit}>
        <InputGroup>
            <Label>{t('form.user.name')}</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder={t('form.user.namePlaceholder')}
              required
              disabled={isLoading}
            />
          </InputGroup>

          <InputGroup>
            <Label>{t('form.user.email')}</Label>
            <Input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t('form.user.emailPlaceholder')}
              required
              disabled={isLoading}
              className={emailError ? 'error-input' : ''}
            />
            {emailError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {emailError}
              </div>
            )}
          </InputGroup>

          <InputGroup>
            <Label>{t('form.user.password')}</Label>
            <Input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder={t('form.user.passwordPlaceholder')}
              required={!id} // Senha só é obrigatória na criação
              disabled={Boolean(id) || isLoading}
              className={passwordError ? 'error-input' : ''}
            />
            {passwordError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {passwordError}
              </div>
            )}
          </InputGroup>

          <InputRow>
            <InputGroup>
              <Label>{t('form.user.profile')}</Label>
              <Select
                name="profile"
                value={formData.profile}
                onChange={handleChange}
                required
                disabled={isLoading}
              >
                <option value="">{t('form.user.profilePlaceholder')}</option>
                {/* Managers só podem criar usuários normais */}
                {hasAdminAccess() && <option value="admin">{t('form.user.profileAdmin')}</option>}
                <option value="user">{t('form.user.profileUser')}</option>
                {hasAdminAccess() && <option value="manager">{t('form.user.profileManager')}</option>}
              </Select>
            </InputGroup>

            <InputGroup>
              <Label>{t('form.user.organization')} {isManager() && `(${t('form.user.organizationPredefined')})`}</Label>
              <Select
                name="organization"
                value={formData.organization || managerOrgName}
                onChange={handleChange}
                required
                disabled={isLoading || isManager()} // managers não podem escolher outra org
              >
                {isManager() ? (
                  // Para managers, mostra apenas sua organização
                  <option value={managerOrgName}>{managerOrgName || t('form.user.loadingOrganization')}</option>
                ) : (
                  // Para admins, mantém a opção de selecionar
                  <>
                    <option value="">{t('form.user.organizationPlaceholder')}</option>
                    {organizations.map(org => (
                      <option key={org._id} value={org.nameOrganization}>
                        {org.nameOrganization}
                      </option>
                    ))}
                  </>
                )}
              </Select>
            </InputGroup>
          </InputRow>

          <InputGroup>
            <Label>{t('form.user.phone')}</Label>
            <Input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('form.user.phonePlaceholder')}
              required
              disabled={isLoading}
              className={phoneError ? 'error-input' : ''}
              maxLength={19}
            />
            {phoneError && (
              <div style={{ 
                color: theme.error, 
                fontSize: '12px', 
                marginTop: '5px' 
              }}>
                {phoneError}
              </div>
            )}
          </InputGroup>

          <InputGroup>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                id="activeMfa"
                name="activeMfa"
                checked={formData.activeMfa}
                onChange={handleChange}
                disabled={isLoading}
                style={{ width: 'auto' }}
              />
              <Label htmlFor="activeMfa" style={{ marginBottom: 0, cursor: 'pointer' }}>
                {t('form.user.mfaActive')}
              </Label>
            </div>
            <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '4px' }}>
              {t('form.user.mfaDescription')}
            </div>
          </InputGroup>

          {/* Campo oculto para manter o valor de role no estado */}
          <input
            type="hidden"
            name="role"
            value={formData.role}
          />

          <SubmitButton 
            type="submit" 
            disabled={isLoading || isSubmitting}
          >
            {isLoading || isSubmitting ? t('form.user.processing') : id ? t('form.user.update') : t('form.user.add')}
          </SubmitButton>
        </Form>
      </FormContainer>

      {showConfirmModal && (
        <AnimatedModal>
          <AnimatedModalContent>
            <h3>{id ? t('form.user.updateConfirmation') : t('form.user.addConfirmation')}</h3>
            <p>{id ? t('form.user.updateConfirmText') : t('form.user.addConfirmText')}</p>
            <ConfirmModalButtons>
              <CancelButton onClick={() => setShowConfirmModal(false)}>
                {t('common.cancel')}
              </CancelButton>
              <GreenConfirmButton onClick={confirmSubmit}>
                {id ? t('form.user.update') : t('form.user.add')}
              </GreenConfirmButton>
            </ConfirmModalButtons>
          </AnimatedModalContent>
        </AnimatedModal>
      )}
    </Container>
  );
};

export default UserForm; 